import type { LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/modern/utils";

import { NOTIFICATION_TILE_MOTION_CLS } from "../constants/layout";
import { toneStyle, TILE_SURFACE_CLS, TILE_MUTED_SURFACE_CLS } from "../constants/tones";

/**
 * Shared pair gap: heading→description and count→caption.
 * Kept small so the label sits close to its supporting line.
 */
const PAIR_GAP_CLS = "mt-1";

export function NotificationTileButton({
  onClick,
  eyebrow,
  value,
  caption,
  description,
  icon: Icon,
  classes,
  muted,
  big,
  className,
  span,
  children,
  captionClassName,
  eyebrowClassName,
  descriptionClassName,
  valueClassName,
}: {
  onClick?: () => void;
  eyebrow: string;
  value: ReactNode;
  caption?: string;
  /** Optional copy under the label (eyebrow), above the value. */
  description?: string;
  icon?: LucideIcon;
  classes?: { bg: string; text: string; ring: string };
  muted?: boolean;
  big?: boolean;
  className?: string;
  span?: string;
  children?: ReactNode;
  captionClassName?: string;
  eyebrowClassName?: string;
  descriptionClassName?: string;
  valueClassName?: string;
}) {
  const style: CSSProperties | undefined =
    muted || !classes ? undefined : toneStyle(classes.bg);

  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] p-5 text-left",
        NOTIFICATION_TILE_MOTION_CLS,
        muted ? TILE_MUTED_SURFACE_CLS : TILE_SURFACE_CLS,
        big ? "min-h-[11rem]" : "min-h-[7.5rem]",
        span,
        className,
      )}
    >
      <div className="min-h-0 min-w-0 shrink-0 overflow-hidden">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "text-[10px] font-bold uppercase leading-none tracking-[0.18em] text-foreground/70",
              eyebrowClassName,
            )}
          >
            {eyebrow}
          </span>
          {Icon ? (
            <Icon className="size-5 shrink-0 opacity-80" aria-hidden />
          ) : null}
        </div>
        {description ? (
          <p
            className={cn(
              PAIR_GAP_CLS,
              "overflow-hidden text-sm font-normal leading-snug text-foreground/75",
              descriptionClassName,
              big ? "line-clamp-4" : "line-clamp-2",
            )}
          >
            {description}
          </p>
        ) : null}
      </div>

      {/*
        Count stays bottom-anchored (mosaic fill). pt-9 = 3× former mt-3 between
        description and count when the tile is short; tall tiles add flex space.
      */}
      <div className="relative z-[1] mt-auto min-w-0 shrink-0 pt-9">
        <div
          className={cn(
            "font-bold leading-[0.95] tracking-tight",
            big ? "text-5xl sm:text-6xl" : "text-4xl",
            valueClassName,
          )}
        >
          {value}
        </div>
        {caption ? (
          <p
            className={cn(
              PAIR_GAP_CLS,
              "text-sm font-medium leading-snug text-foreground/75",
              captionClassName,
            )}
          >
            {caption}
          </p>
        ) : null}
        {children}
      </div>
    </button>
  );
}
